import { creatorbase, Operator } from '@round/api';
import { SortingState } from '@tanstack/react-table';
import { countryOptions } from 'utility/constants';
import {
    getLastUsedOptionForDays,
    getPostingFrequencyTierFromUrlState,
    maxAverageDraftTimeOptions,
} from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';
import { getTiktokIDFromIdPath } from 'Modules/Plans/CreatorSearch/components/filters/TiktokAudioUrlFilter/helpers';
import {
    Filters as ExploreFilters,
    minMax,
    parseContentTagsFromApi,
    serializeAudienceLocationsToApi,
    serializeContentTagsToApi,
} from 'Modules/Plans/CreatorSearch/utils/filters';
import { LocationItem } from 'Modules/Plans/CreatorSearch/components/filters';

export type AddToCampaignFilters = Omit<ExploreFilters, 'platforms'>;

export function mapPersistedCampaignFilterParamsToExploreFilters(
    filters: Partial<creatorbase.CampaignCreatorExploreFilters>
): AddToCampaignFilters {
    const lastUsedOption =
        typeof filters.min_last_used_days === 'number'
            ? getLastUsedOptionForDays(filters.min_last_used_days)
            : undefined;

    // Persisted as the API param string
    const parsedAudienceLocations:
        | { operator?: Operator; conditions: { country: string; min_percentage: number }[] }
        | undefined = filters.audience_location ? JSON.parse(filters.audience_location) : undefined;

    return {
        contentTags: parseContentTagsFromApi(filters.content_tags),
        locations: filters.primary_country
            ?.split(',')
            .filter(Boolean)
            .map((code): LocationItem => {
                const option = countryOptions.find((c) => c.value === code);
                return { value: code, label: option?.label ?? code };
            }),
        followerRange: minMax(filters.min_follower_count, filters.max_follower_count),
        avgViews: minMax(filters.min_avg_video_views_per_post, filters.max_avg_video_views_per_post),
        estimatedCpe: minMax(filters.min_estimated_cpe, filters.max_estimated_cpe),
        avgEngagementRate: minMax(
            typeof filters.min_avg_engagement_rate === 'number'
                ? Math.round(filters.min_avg_engagement_rate * 100 * 100) / 100
                : undefined,
            typeof filters.max_avg_engagement_rate === 'number'
                ? Math.round(filters.max_avg_engagement_rate * 100 * 100) / 100
                : undefined
        ),
        costRange: minMax(filters.min_cost, filters.max_cost),
        hasCost: typeof filters.has_cost === 'boolean' ? filters.has_cost : true,
        responseRate:
            typeof filters.min_response_rate === 'number'
                ? Math.round(filters.min_response_rate * 100 * 100) / 100
                : undefined,
        avgDraftTime: maxAverageDraftTimeOptions.find((o) => o.value === filters.max_avg_draft_time_in_hours),
        lastUsed: lastUsedOption,
        postingFrequency: getPostingFrequencyTierFromUrlState(filters.posting_frequency),
        tiktokAudioIds: filters.has_used_tiktok_audio_tiktok_ids
            ? filters.has_used_tiktok_audio_tiktok_ids
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : undefined,
        // Since we persist the conditions as the API expects them, we map back to the Filter options type here
        audienceLocations: parsedAudienceLocations
            ? {
                  ...parsedAudienceLocations,
                  conditions: parsedAudienceLocations.conditions.map((c) => ({
                      countryCode: c.country,
                      minPercentage: c.min_percentage,
                  })),
              }
            : undefined,
        spotifyTrackIds: filters.spotify_ids
            ? filters.spotify_ids
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : undefined,
        spotifyGenres: filters.genres
            ? filters.genres
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : undefined,
        spotifyArtistIds: filters.artist_ids
            ? filters.artist_ids
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : undefined,
    };
}

export function mapExploreFiltersToPersistedCampaignFilterParams(
    exploreFilters: AddToCampaignFilters
): Partial<creatorbase.CampaignCreatorExploreFilters> {
    return {
        min_follower_count: exploreFilters.followerRange?.min,
        max_follower_count: exploreFilters.followerRange?.max,
        min_avg_video_views_per_post: exploreFilters.avgViews?.min,
        max_avg_video_views_per_post: exploreFilters.avgViews?.max,
        min_cost: exploreFilters.costRange?.min,
        max_cost: exploreFilters.costRange?.max,
        primary_country: exploreFilters.locations?.length
            ? exploreFilters.locations.map((l) => l.value).join(',')
            : undefined,
        content_tags: serializeContentTagsToApi(exploreFilters.contentTags),
        has_cost: exploreFilters.hasCost,
        max_avg_draft_time_in_hours: exploreFilters.avgDraftTime?.value,
        min_response_rate:
            typeof exploreFilters.responseRate === 'number' ? exploreFilters.responseRate / 100 : undefined,
        min_last_used_days: exploreFilters.lastUsed?.days,
        has_used_tiktok_audio_tiktok_ids: exploreFilters.tiktokAudioIds?.length
            ? exploreFilters.tiktokAudioIds
                  .map((id) => getTiktokIDFromIdPath(id))
                  .filter(Boolean)
                  .join(',') || undefined
            : undefined,
        min_estimated_cpe: exploreFilters.estimatedCpe?.min,
        max_estimated_cpe: exploreFilters.estimatedCpe?.max,
        min_avg_engagement_rate:
            typeof exploreFilters.avgEngagementRate?.min === 'number'
                ? exploreFilters.avgEngagementRate.min / 100
                : undefined,
        max_avg_engagement_rate:
            typeof exploreFilters.avgEngagementRate?.max === 'number'
                ? exploreFilters.avgEngagementRate.max / 100
                : undefined,
        spotify_ids: exploreFilters.spotifyTrackIds?.join(','),
        genres: exploreFilters.spotifyGenres?.join(','),
        artist_ids: exploreFilters.spotifyArtistIds?.join(','),
        audience_location: serializeAudienceLocationsToApi(exploreFilters.audienceLocations),
        posting_frequency: exploreFilters.postingFrequency,
    };
}

export const mapSortToApiParams = (sorting: SortingState) => {
    return {
        order_by_asc: sorting.find((sort) => sort.desc === false)?.id || undefined,
        order_by_desc: sorting.find((sort) => sort.desc === true)?.id || undefined,
    };
};

export const mapApiParamsToSort = ({
    order_by_asc,
    order_by_desc,
}: {
    order_by_asc?: string;
    order_by_desc?: string;
}): SortingState => {
    return [
        ...(order_by_asc ? [{ id: order_by_asc, desc: false }] : []),
        ...(order_by_desc ? [{ id: order_by_desc, desc: true }] : []),
    ];
};

export type ClientInfluencerData = {
    clientInfluencerAccountId: number;
    cost: number | null;
    currencyId: number | null;
};
